import { defineNuxtRouteMiddleware, navigateTo, useNuxtApp } from 'nuxt/app'

import { useLocationStore } from '@common/location-layer/store/location.store'

import type { Pinia } from 'pinia'

/** Route name Nuxt gives `pages/[...notFound].vue`: every path ClientPortal has no page for. */
const CATCH_ALL_ROUTE_NAME = 'notFound'

/**
 * On an AI-built portal's host, a client-side navigation to a path ClientPortal has no page for
 * leaves the app with a full page load of the same URL.
 *
 * There an edge splits the host's paths between the AI-built app and ClientPortal: a post-login
 * `?redirect=/schedule` or a link in content may name one of the app's own pages, which only a real
 * request reaches. Other portals keep the in-app not-found landing.
 *
 * Runs first so the auth gate does not ask for a sign-in on a page ClientPortal does not own.
 * Never on the initial load (server render or hydration): that request already reached the server,
 * and leaving again would loop when nothing else serves the path.
 */
export default defineNuxtRouteMiddleware(to => {
  if (process.server) return

  const nuxtApp = useNuxtApp()
  const pinia = nuxtApp.$pinia as Pinia | undefined
  if (nuxtApp.isHydrating || !pinia || to.name !== CATCH_ALL_ROUTE_NAME) return
  if (!useLocationStore(pinia).aiBuilder) return

  return navigateTo(to.fullPath, { external: true })
})
